// presentation/hooks/useEquipo.ts
import { useCallback, useState } from 'react';
import { container } from '@/di/container';
import { Equipo } from '@/domain/entities';
import {
  CreateEquipoDTO,
  EquipoParamsDTO,
} from '@/application/dto';
import {
  CreateEquipoUseCase,
  GetEquipoByIdUseCase,
  GetEquiposUseCase,
  UpdateEquipoUseCase,
} from '@/application/use-cases';


export function useEquipo() {
  const [Equipo, setEquipo] =
    useState<Equipo | null>(null);

  const [data, setData] =
    useState<Equipo[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState<string>();

  const clearError = () => {
    setError(undefined);
  };

  const runRequest = useCallback(
    async <T,>(operation: () => Promise<T>, errorMessage: string) => {
      try {
        setLoading(true);
        setError(undefined);
        return await operation();
      } catch (err) {
        setError(err instanceof Error ? err.message : errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadById = useCallback(
    async (id: number | string) => {
      const result = await runRequest(
        () => container
          .get<GetEquipoByIdUseCase>('GetEquipoByIdUseCase')
          .execute(String(id)),
        'Error al cargar el equipo'
      );

      setEquipo(result);
      return result;
    },
    [runRequest]
  );

  const loadAll = useCallback(
    async (
      params: EquipoParamsDTO
    ) => {
      const result = await runRequest(
        () => container
          .get<GetEquiposUseCase>('GetEquipoUseCase')
          .execute(params),
        'Error al cargar los equipos'
      );

      setData(result);
      return result;
    },
    [runRequest]
  );

  const create = useCallback(
    (data: CreateEquipoDTO) => runRequest(
      () => container
        .get<CreateEquipoUseCase>('CreateEquipoUseCase')
        .execute(data),
      'Error al crear el equipo'
    ),
    [runRequest]
  );

  const update = useCallback(
    async (id: string, data: CreateEquipoDTO) => {
      const result = await runRequest(
        () => container
          .get<UpdateEquipoUseCase>('UpdateEquipoUseCase')
          .execute(id, data),
        'Error al actualizar el equipo'
      );

      setEquipo(result);
      setData((current) => current.map((item) =>
        String(item.id) === id ? result : item
      ));

      return result;
    },
    [runRequest]
  );

  return {
    Equipo,
    data,
    loading,
    error,
    loadById,
    loadAll,
    create,
    update,
    clearError,
  };
}
